<template>

	<nav id="js-facets" class="g-facets" v-if="visibleFacets">
		<div class="g-facets__overlay" tabindex="-1" data-micromodal-close>
			<div class="g-facets__content" role="dialog" aria-modal="true" aria-labelledby="g-facets-title">
				<button type="button" class="g-button--no-styling u-icon-cross g-facets__close-button" aria-label="Close Filters" data-micromodal-close></button>
				<div class="g-facets__form">
					<div id="g-facets-title" class="g-facets__heading-section">
						<div class="g-facets__heading-container">
							<div class="g-facets__heading-inner">
								<h2 class="g-facets__heading">
									<span class="g-facets__heading-icon u-icon-filter" aria-hidden="true" focusable="false"></span>
									<span class="g-facets__heading-text">Filter <span v-if="appliedFiltersCount"> ({{ appliedFiltersCount }})</span></span>
								</h2>
								<a v-if="appliedFiltersCount" class="g-facets__clear-all-link" @click="clearFacets()">Clear All</a>
							</div>
						</div>
						<div v-if="pagination && pagination.totalResults > 0" class="g-facets__product-count">
							<span class="g-product-list__product-count">{{ pagination.totalResults }} {{ pagination.totalResults > 1 ? 'Items' : 'Item' }}</span>
						</div>
						<div class="g-facets__applied-items" v-if="appliedFiltersCount">
							<ul class="g-facets__list">
								<li class="g-facets__applied-item" v-for="filter in appliedFilters" :key="filter.filterValue">
									<button type="button" class="g-facets__applied-label" @click="toggleFilter(filter.field, typeof filter.value !== 'object' ? filter.value : [filter.value.rangeLow, filter.value.rangeHigh])">
										<span class="g-facets__applied-label-icon u-icon-cross"></span>
										<span class="g-facets__applied-label-text">{{ filter.filterValue }}</span>
									</button>
								</li>
							</ul>
						</div>
					</div>
					<div class="g-facets__list-section">
						<ul class="g-facets__list">
							<li class="g-facet-set" :class="{'g-facet-set--collapsed': facet.collapse}" v-for="facet in visibleFacets" :key="facet.field" v-if="facet.field != 'code' && (facet.type !== 'hierarchy' || (facet.type === 'hierarchy' && (hierarchyBreadcrumbValues || facet.values?.length)))">
								<h3 v-if="facet.nested" class="g-facet-set__heading">
									<div class="g-facet-set__label g-facet-set__label--nested">
										<span class="g-facet-set__heading-label g-facet-set__heading-tippy" v-if="facet._custom?.tippy" v-tippy :title="facet._custom?.tippy">
											{{ facet.label }}
										</span>
										<span class="g-facet-set__heading-label" v-else>
											{{ facet.label }}
										</span>
									</div>
								</h3>
								<h3 v-else class="g-facet-set__heading" @click="facet.collapse = !facet.collapse">
									<button type="button" :id="`facet-control-${facet.field}`" class="g-facet-set__label g-button--no-styling" :aria-controls="`facet-section-${facet.field}`" :aria-expanded="facet.collapse ? 'false' : 'true'">
										<span class="g-facet-set__heading-label g-facet-set__heading-tippy" v-if="facet._custom?.tippy" v-tippy :title="facet._custom?.tippy">
											{{ facet.label }}
										</span>
										<span class="g-facet-set__heading-label" v-else>
											{{ facet.label }}
										</span>
									</button>
								</h3>

								<!-- Bloom Color -->
								<div :id="`facet-section-${facet.field}`" class="g-facet-set__list" role="region" :aria-labelledby="`facet-control-${facet.field}`" v-if="(facet.field === 'bloom_color') && facet.values.length > 0">
									<div class="g-facet-set__color">
										<label :class="['g-facet-set__list-item', 'g-form-checkbox']" v-for="(value, index) in facet.values" :key="value.label">
											<input class="g-form-checkbox__input" v-model="value.active" type="checkbox" @click="toggleFilter(facet.field, value.type === 'value' ? value.value : [value.low, value.high])">
											<span class="g-form-checkbox__color" :title="value.label">
												<span class="g-form-checkbox__color--inner" :class="[`g-form-checkbox__color--inner--${value.label?.toLowerCase?.().replace(/[^\w ]+/g, '').replace(/ +/g, '-')}`]">
													<span class="u-hide-visually">{{ value.label }} ({{ value.count }})</span>
												</span>
											</span>
										</label>
									</div>
								</div>

								<!-- Advantages -->
								<div :id="`facet-section-${facet.field}`" class="g-facet-set__list" role="region" :aria-labelledby="`facet-control-${facet.field}`" v-else-if="(facet.field === 'ss_advantages') && facet.values.length > 0">
									<div class="g-facet-set__advantages">
										<label :class="['g-facet-set__list-item', 'g-form-checkbox']" v-for="(value, index) in facet.values" :key="value.label">
											<input class="g-form-checkbox__input" v-model="value.active" type="checkbox" @click="toggleFilter(facet.field, value.type === 'value' ? value.value : [value.low, value.high])">
											<span class="g-form-checkbox__advantage">
												<span class="facet-advantage" v-tippy :title="value._custom?.title" :class="[value._custom?.class]" v-if="value._custom?.image">
													<img :src="value._custom.image" :alt="value._custom?.title" class="facet-advantage-image" loading="lazy">
												</span>
												<span class="facet-advantage" v-tippy :title="value._custom?.title" :class="[value._custom?.class]" v-else>
													{{ value._custom?.label || value.label }}
												</span>
											</span>
										</label>
									</div>
								</div>

								<!-- Hierarchy facets -->
								<div :id="`facet-section-${facet.field}`" class="g-facet-set__list" role="region" :aria-labelledby="`facet-control-${facet.field}`" v-else-if="facet.type === 'hierarchy'">
									<div :class="`g-facet-set__list-item g-facet-set__list-item--no-link g-facet-set__list-item--level-${index === 0 ? '1' : '2'}`" v-if="hierarchyBreadcrumbValues" v-for="(breadcrumb, index) in hierarchyBreadcrumbValues" :key="index">
										<span class="g-facet-set__list-item-name g-facet-set__list-item-name--applied">{{ breadcrumb }} {{ hierarchyBreadcrumbValuesLast }}</span>
									</div>

									<div :class="['g-facet-set__list-item', `g-facet-set__list-item--level-${hierarchyBreadcrumbValues ? '2' : '1'}`, {'u-hidden': index + 1 > facetShowCount}]" v-for="(value, index) in facet.values" :key="value.label">
										<button type="button" class="g-facet-set__list-item-link g-button--no-styling" @click="toggleFilter(facet.field, value.type === 'value' ? value.value : [value.low, value.high])">
											<span :class="['g-facet-set__list-item-name', {'g-facet-set__list-item-name--applied': value.active}]">{{ value.label }}</span>
											<span class="g-facet-set__list-item-count">({{ value.count }})</span>
										</button>
									</div>

									<div v-if="facet.values.length > facetShowCount" class="g-facet-set__list-show-more">
										<button type="button" class="g-facet-set__list-show-more-link g-button--no-styling" @click="viewMoreClick">View More</button>
									</div>
								</div>

								<!-- Nested facets -->
								<div :id="`facet-section-${facet.field}`" class="g-facet-set__list g-facet-set__list--nested" v-else-if="facet.nested">
									<div :class="['g-facet-set__list-nested-item', {'g-facet-set--collapsed': facetItem.collapse}]" v-for="facetItem in facet.items" :key="facetItem.field">
										<h4 class="g-facet-set__heading" @click="facetItem.collapse = !facetItem.collapse">
											<button type="button" :id="`facet-control-${facetItem.field}`" class="g-facet-set__label g-button--no-styling" :aria-controls="`facet-section-${facetItem.field}`" :aria-expanded="facetItem.collapse ? 'false' : 'true'">
												{{ facetItem.label }}
											</button>
										</h4>

										<div :id="`facet-section-${facetItem.field}`" class="g-facet-set__list" role="region" :aria-labelledby="`facet-control-${facetItem.field}`" v-if="(facetItem.type === 'list' || facetItem.type == null) && facetItem.values.length > 0">
											<label :class="['g-facet-set__list-item', 'g-form-checkbox', {'u-hidden': index + 1 > facetShowCount}]" v-for="(value, index) in facetItem.values" :key="value.label">
												<input class="g-form-checkbox__input" v-model="value.active" type="checkbox" @click="toggleFilter(facetItem.field, value.type === 'value' ? value.value : [value.low, value.high])">
												<span class="g-facet-set__list-item-link g-form-checkbox__caption">
													<span class="g-facet-set__list-item-name" v-tippy :title="value._custom?.title">{{ value.label }}</span>
													<span class="g-facet-set__list-item-count">({{ value.count }})</span>
												</span>
											</label>

											<div v-if="facetItem.values.length > facetShowCount" class="g-facet-set__list-show-more">
												<button type="button" class="g-facet-set__list-show-more-link g-button--no-styling" @click="viewMoreClick">View More</button>
											</div>
										</div>
									</div>
								</div>

								<!-- Non-hierarchy facets -->
								<div :id="`facet-section-${facet.field}`" class="g-facet-set__list" role="region" :aria-labelledby="`facet-control-${facet.field}`" v-else-if="(facet.type === 'list' || facet.type == null) && facet.values.length > 0">
									<label :class="['g-facet-set__list-item', 'g-form-checkbox', {'u-hidden': index + 1 > facetShowCount}]" v-for="(value, index) in facet.values" :key="value.label">
										<input class="g-form-checkbox__input" v-model="value.active" type="checkbox" @click="toggleFilter(facet.field, value.type === 'value' ? value.value : [value.low, value.high])">
										<span class="g-facet-set__list-item-link g-form-checkbox__caption">
											<span class="g-facet-set__list-item-name" v-tippy :title="value._custom?.title">{{ value.label }}</span>
											<span class="g-facet-set__list-item-count">({{ value.count }})</span>
										</span>
									</label>

									<div v-if="facet.values.length > facetShowCount" class="g-facet-set__list-show-more">
										<button type="button" class="g-facet-set__list-show-more-link g-button--no-styling" @click="viewMoreClick">View More</button>
									</div>
								</div>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</nav>

</template>

<script>

	import Search from '../../Search';

	export default {
		data() {
			return {
				facetShowCount: 9,
				hierarchy: {
					breadcrumb: {},
					facet: {},
				},
				mivaJS: window.mivaJS,
				uniqueId: ''
			}
		},
		props: {
			breadcrumbs: {
				default() {
					return [];
				}
			},
			facets: {
				default() {
					return [];
				}
			},
			filterSummary: {
				default() {
					return [];
				}
			},
			pagination: {
				default() {
					return [];
				}
			}
		},
		computed: {
			appliedFilters () {
				let filters = [];

				if (this.hierarchyBreadcrumbValues && this.hierarchy.facet?.hierarchyDelimiter) {
					filters.push({
						field: 'hierarchy',
						filterLabel: 'Category',
						filterValue: this.hierarchyBreadcrumbValues.join(` ${this.hierarchy.facet.hierarchyDelimiter} `),
						label: `Category: ${this.hierarchy.breadcrumb?.filterValue}`,
						value: this.hierarchy.breadcrumb?.filterValue
					});
				} else if (this.hierarchy.facet?.facet_active && this.hierarchy.facet?.values?.length) {
					this.hierarchy.facet.values.forEach((facetValue) => {
						if (!facetValue.active || !facetValue.value?.length) {
							return;
						}

						filters.push({
							field: 'hierarchy',
							filterLabel: 'Category',
							filterValue: facetValue.label,
							label: `Category: ${facetValue.label}`,
							value: facetValue.value,
						});

						if (!Search.client.states.search.filters.includes(searchFilter => searchFilter.field === 'hierarchy' && searchFilter.value !== facetValue.value)) {
							Search.client.states.search.filters.push({
								field: 'hierarchy',
								type: 'filter',
								value: facetValue.value
							});
						}
					});
				}

				if (this.isCategoryPage() && !filters.length) {
					Search.client.states.search.filters.forEach((searchFilter) => {
						if (searchFilter.field !== 'hierarchy' || searchFilter.type !== 'filter') {
							return;
						}

						const label = searchFilter.value.split(this.hierarchy.facet.hierarchyDelimiter).pop();

						filters.push({
							field: 'hierarchy',
							filterLabel: 'Category',
							filterValue: label,
							label: `Category: ${label}`,
							value: searchFilter.value,
						});
					});
				}

				return filters.concat(this.filterSummary);
			},
			appliedFiltersCount () {
				return this.appliedFilters.length;
			},
			hierarchyBreadcrumb () {
				return this.breadcrumbs.find(breadcrumb => breadcrumb.field === 'hierarchy');
			},
			hierarchyBreadcrumbValues () {
				if (!this.hierarchy.breadcrumb?.filterValue || !this.hierarchy.facet?.hierarchyDelimiter) {
					return;
				}

				const breadcrumbValues = this.hierarchy.breadcrumb.filterValue.split(this.hierarchy.facet.hierarchyDelimiter);

				if (breadcrumbValues && this.isCategoryPage()) {
					return breadcrumbValues.filter((breadcrumbValue) => {
						return breadcrumbValue !== this.mivaJS.Category_Hierarchy;
					});
				}

				return breadcrumbValues;
			},
			visibleFacets () {
				const nestedFacets = [
					{
						_custom: {
							tippy: 'Filter for species native to your state. Given appropriate habitat and climate, native plants can be grown outside their range'
						},
						field: 'native_range',
						fields: [
							'ss_midwest',
							'ss_northeast',
							'ss_south',
							'ss_great_plains',
							'ss_west'
						],
						items: [],
						label: 'Native Range',
						nested: true
					}
				];

				const visibleFacets = this.facets.filter((facet, index) => {
					if (facet.label.indexOf('(hidden)') > -1 || (facet.type === 'hierarchy' && !this.isCategoryPage()) || facet.nested) {
						return false;
					}

					facet._custom = {
						tippy: null
					};

					if (facet.field === 'sun_exposure') {
						facet._custom.tippy = this.mivaJS?.DetailsTooltips?.sun_exposure;
					} else if (facet.field === 'search_spring_ht') {
						facet._custom.tippy = `Approximate plant height for mature, full-grown plants in flower. Actual height will vary considerably due to competition, sun & soil conditions, plant age, and weather.`;
					} else if (facet.field === 'bloom_time') {
						facet._custom.tippy = `Range of months the plant is expected to bloom; not a continual bloom.`;
					} else if (facet.field === 'ss_usda_zones_facet') {
						facet._custom.tippy = `Please treat the planting zone range as a guideline only.`;
					} else if (facet.field === 'ss_germination_code_facet') {
						facet._custom.tippy = this.mivaJS?.DetailsTooltips?.germination_code;

						for (const facetValue of facet.values) {
							facetValue._custom = {
								title: null
							};

							if (facetValue.label === 'A') {
								facetValue._custom.title = `<strong>No pre-treatment needed.</strong> Seeds germinate after sowing in a warm location.`;
							} else if (facetValue.label === 'B') {
								facetValue._custom.title = `<strong>Hot water treatment:</strong> Bring water to a boil. Pour over seed and then allow seed to soak for 24 hours at room temp prior to planting or starting artificial treatment.`;
							} else if (facetValue.label === 'C(10)') {
								facetValue._custom.title = `<strong>Seeds germinate after ~10 days cold, moist stratification.</strong> (A common method of breaking dormancy naturally for species requiring cold moist stratification is to sow seeds outdoors fall-winter.) Artificial Stratification: Mix seeds with equal amounts or more of damp sand, vermiculite, or other sterile media. Place mixture in a labeled, sealed plastic bag and store in a refrigerator (33-38&deg;F.) Stratify for ~10 days. Some seeds may sprout in the bag if moist stratified too long. If sprouting occurs, plant immediately.`;
							} else if (facetValue.label === 'C(30)') {
								facetValue._custom.title = `<strong>Seeds germinate after ~30 days cold, moist stratification.</strong> (A common method of breaking dormancy naturally for species requiring cold moist stratification is to sow seeds outdoors fall-winter.) Artificial Stratification: Mix seeds with equal amounts or more of damp sand, vermiculite, or other sterile media. Place mixture in a labeled, sealed plastic bag and store in a refrigerator (33-38&deg;F). Stratify for ~30 days. Some seeds may sprout in the bag if moist stratified too long. If sprouting occurs, plant immediately.`;
							} else if (facetValue.label === 'C(60)') {
								facetValue._custom.title = `<strong>Seeds germinate after ~60 days cold, moist stratification.</strong> (A common method of breaking dormancy naturally for species requiring cold moist stratification is to sow seeds outdoors fall-winter.) Artificial Stratification: Mix seeds with equal amounts or more of damp sand, vermiculite, or other sterile media. Place mixture in a labeled, sealed plastic bag and store in a refrigerator (33-38&deg;F.) Stratify for ~60 days. (Two months of this cold, moist treatment is normally required to break the dormancy of these seeds, but one month may work for many species if time is a constraint.) Some seeds may sprout in the storage bag if moist stratified too long. If sprouting occurs, plant immediately.`;
							} else if (facetValue.label === 'C(90)') {
								facetValue._custom.title = `<strong>Seeds germinate after ~90 days cold, moist stratification.</strong> (A common method of breaking dormancy naturally for species requiring cold moist stratification is to sow seeds outdoors fall-winter.) Artificial Stratification: Mix seeds with equal amounts or more of damp sand, vermiculite, or other sterile media. Place mixture in a labeled, sealed plastic bag and store in a refrigerator (33-38&deg;F.) Stratify for ~90 days. (Less days may work for many species if time is a constraint.) Some seeds may sprout in the storage bag if moist stratified too long. If sprouting occurs, plant immediately. `;
							} else if (facetValue.label === 'C(120)') {
								facetValue._custom.title = `<strong>Seeds germinate after ~120 days cold, moist stratification.</strong> (A common method of breaking dormancy naturally for species requiring cold moist stratification is to sow seeds outdoors fall-winter.) Artificial Stratification: Mix seeds with equal amounts or more of damp sand, vermiculite, or other sterile media. Place mixture in a labeled, sealed plastic bag and store in a refrigerator (33-38&deg;F.) Stratify for ~120 days. (Less days may work for many species if time is a constraint.) Some seeds may sprout in the storage bag if moist stratified too long. If sprouting occurs, plant immediately.`;
							} else if (facetValue.label === 'D') {
								facetValue._custom.title = `<strong>Seeds are very small or need light to germinate:</strong> Seeds should be surface sown - no soil cover or just a dusting of soil should be applied. If grown in outdoor garden beds, sow seed on level surface. Cover with a single layer of burlap or cotton sheet. Remove cover after germination. Do not let soil dry out until seedlings are established. Shading with a window screen set 12in above soil the first year will help prevent drying. If sowing seeds in containers, water from the bottom as necessary.`;
							} else if (facetValue.label === 'E') {
								facetValue._custom.title = `<strong>Double dormancy (warm-cold):</strong> Seeds need a warm, moist period followed by a cold, moist period: Sow seeds outdoors in the spring and expect germination in 1 year, the following spring. To artificially mimic this stratification process, mix seeds with sterile media. Place mixture in a sealed plastic bag in a warm place (about 80 degrees F) for 60-90 days. Then place in the refrigerator for another 60-90 days before sowing.`;
							} else if (facetValue.label === 'F') {
								facetValue._custom.title = `<strong>Double dormancy (cold-warm-cold):</strong> Seeds need a cold, moist period followed by a warm, moist period followed by a 2nd cold, moist period: Sow seeds outdoors in the fall and expect germination after 2 years. To artificially mimic this stratification process, start by following instructions for code C for 60-90 days, then store in warm place (about 80 degrees F) for 60-90 days followed by a 2nd cold, moist period in the refrigerator.`;
							} else if (facetValue.label === 'G') {
								facetValue._custom.title = `<strong>Seeds germinate most successfully in cool soil:</strong> Sow seeds in late fall (after hard frost) through early spring.`;
							} else if (facetValue.label === 'H') {
								facetValue._custom.title = `<strong>Seeds need scarification:</strong> Seed purchased from Prairie Moon is not scarified. We can scarify, on request, bulk amounts of 1/8 oz or more. To scarify, rub seed between 2 sheets of medium grit sandpaper. The goal is to abrade seed coats - stop if seeds are being crushed. Scarification should be done before stratification (code C) if needed. Fall or winter sown seed should not be scarified to prevent the chance of premature germination and winter kill.`;
							} else if (facetValue.label === 'I') {
								facetValue._custom.title = `<strong>Legume.</strong> Rhizobium Inoculum may be added`;
							} else if (facetValue.label === 'J') {
								facetValue._custom.title = `<strong>We remove the hulls from these legume seeds.</strong> If you have unhulled seed from another source, treat as in code H (needs scarification).`;
							} else if (facetValue.label === 'K') {
								facetValue._custom.title = `<strong>Hemiparasitic species</strong> which needs a host plant: Good hosts for many (but not all) parasitic species include low growing grasses and sedges. With a knife, make a 2 in deep cut at the base of the host plant. Sow seeds in the cut, making sure it is not more than 1/8 in deep. If the host is transplanted at sowing time, the cut is not needed; damaged roots will be available for attachment by the parasite. You may also try sowing seeds of the host and hemiparasitic species together. To add hemiparasitic species to existing sites, scatter seed on soil surface (rake in if seed is large) in late fall.`;
							} else if (facetValue.label === 'L') {
								facetValue._custom.title = `<strong>Plant fresh seed or keep moist.</strong> Refrigerate until planting.`;
							} else if (facetValue.label === 'M') {
								facetValue._custom.title = `<strong>Best planted outdoors in the fall.</strong>`;
							} else if (facetValue.label === 'O') {
								facetValue._custom.title = `<strong>Impermeable seed coat:</strong> Nick seed coat with a knife, soak in water overnight. Plant.`;
							}
						}
					} else if (facet.field === 'soil_moisture') {
						for (const facetValue of facet.values) {
							facetValue._custom = {
								title: null
							};

							if (facetValue.label === 'Wet') {
								facetValue._custom.title = `Soggy or marshy most of the year.`;
							} else if (facetValue.label === 'Medium-Wet') {
								facetValue._custom.title = `Excessively wet in winter, spring, after heavy rain, but often dries in summer.`;
							} else if (facetValue.label === 'Medium') {
								facetValue.label = `${facetValue.label} (Mesic)`;
								facetValue._custom.title = `Average garden soil. Water soaks in with no run-off.`;
							} else if (facetValue.label === 'Medium-Dry') {
								facetValue._custom.title = `Well drained. Water is removed from soil readily, but not rapidly.`;
							} else if (facetValue.label === 'Dry') {
								facetValue.label = `${facetValue.label} (Xeric)`;
								facetValue._custom.title = `Excessively drained.`;
							}
						}
					} else if (facet.field === 'ss_advantages') {
						for (const facetValue of facet.values) {
							facetValue._custom = {
								title: null,
								image: null,
								label: null,
								class: `facet--${facetValue.label?.toLowerCase?.().replace(/[^\w ]+/g, '').replace(/ +/g, '-')}`
							};

							if (facetValue.label === 'Pollinators') {
								facetValue._custom.title = 'Pollinator Favorite: butterflies, moths, bees, wasps, beetles';
								facetValue._custom.image = `${window.theme_path}public/images/pollinators.png`;
							} else if (facetValue.label === 'Bird Favorite') {
								facetValue._custom.title = 'Bird Favorite: seeds, insects, fruit, nectar, nesting, perch';
								facetValue._custom.image = `${window.theme_path}public/images/bird-favorite.png`;
							} else if (facetValue.label === 'Recommended for Landscaping') {
								facetValue._custom.title = 'Highly recommended for home landscaping';
								facetValue._custom.image = `${window.theme_path}public/images/recommended-for-landscaping.png`;
							} else if (facetValue.label === 'Recommended but Aggressive') {
								facetValue._custom.title = 'Recommended for home landscaping but potentially aggressive; could overwhelm small landscapes';
								facetValue._custom.image = `${window.theme_path}public/images/recommended-but-aggressive.png`;
							} else if (facetValue.label === 'Deer Resistant') {
								facetValue._custom.title = 'Deer Resistant. Deer may browse if food is scarce. Food preferences may vary across the US.';
								facetValue._custom.image = `${window.theme_path}public/images/deer-resistant.png`;
							} else if (facetValue.label === 'Ez') {
								facetValue._custom.title = 'Easy to grow from seed';
								facetValue._custom.label = 'Ez';
							}
						}
					}

					const nestedFacet = nestedFacets.find((nestedFacet) => {
						return nestedFacet.fields.includes(facet.field);
					});

					if (nestedFacet) {
						if (!nestedFacet.hasOwnProperty('index') || nestedFacet.index === null) {
							nestedFacet.index = index;
						}

						nestedFacet.items.push(facet);

						return false;
					}

					return true;
				});

				nestedFacets.forEach((nestedFacet) => {
					if (!nestedFacet.hasOwnProperty('index') || nestedFacet.index === null) {
						return;
					}

					visibleFacets.splice(nestedFacet.index, 0, nestedFacet);
				});

				return visibleFacets;
			}
		},
		watch: {
			hierarchyBreadcrumb (breadcrumb) {
				this.hierarchy.breadcrumb = breadcrumb;
			},
			visibleFacets (facets) {
				this.setHierarchyFacet(facets);
			}
		},
		methods: {
			clearFacets () {
				Search.client.clearFacets().search();
			},
			isCategoryPage () {
				return mivaJS.Screen === 'CTGY';
			},
			setHierarchyBreadcrumb (breadcrumbs) {
				this.hierarchy.breadcrumb = breadcrumbs.find(breadcrumb => breadcrumb.field === 'hierarchy');
			},
			setHierarchyFacet (facets) {
				this.hierarchy.facet = facets.find(facet => facet.type === 'hierarchy');
			},
			toggleFilter (field, value) {
				if (!this.isCategoryPage() && field === 'hierarchy') {
					Search.client.states.search.filters = Search.client.states.search.filters.filter((filter) => {
						return filter.field !== 'hierarchy' || (filter.field === 'hierarchy' && (filter.type === 'bgfilter' || filter.value === value));
					});
				}

				Search.client.filter(field, value).search();

			},
			viewMoreClick (event) {
				event.preventDefault();

				const viewMoreElement = event.currentTarget;
				const setListElement = viewMoreElement.closest('.g-facet-set__list');

				if (!setListElement) {
					return;
				}

				const setListItems = setListElement.querySelectorAll(`.g-facet-set__list-item:not(.g-facet-set__list-show-more)`);

				if (!setListItems) {
					return;
				}

				const viewLessClassName = 'is-show-less';
				const viewMoreText = 'View More';
				const viewLessText = 'View Less';
				let isViewLess = false;

				if (viewMoreElement.classList.contains(viewLessClassName)) {
					viewMoreElement.classList.remove(viewLessClassName);

					viewMoreElement.textContent = viewMoreText;
				} else {
					viewMoreElement.classList.add(viewLessClassName);

					viewMoreElement.textContent = viewLessText;
					isViewLess = true;
				}

				for (let index = 0, len = setListItems.length; index < len; index++) {
					const item = setListItems[index];

					if (index >= this.facetShowCount && !isViewLess) {
						item.classList.add('u-hidden');
					} else {
						item.classList.remove('u-hidden');
					}
				}
			}
		},
		created() {
			this.uniqueId = this._uid;

			this.setHierarchyFacet(this.facets);
			this.setHierarchyBreadcrumb(this.breadcrumbs);
			
		}
	}

</script>
